import { useState, useCallback } from 'react'
import { apiClient } from '@/lib/api.ts'
import {
  Sparkles, Copy, Check, Loader2, X, FileText,
} from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { Button } from '@/components/ui'
import type { Article } from '../shared.ts'
import { formatCurrency } from '../shared.ts'

// ─── AI Texts ───────────────────────────────────────────────────────────────

export function useGenerateSalesText() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<string | null>(null)

  const generate = useCallback(async (
    productName: string,
    productDescription: string,
    tone: 'professional' | 'simple' | 'technical',
  ) => {
    setIsLoading(true)
    setError(null)
    setResult(null)
    try {
      const res = await apiClient.post('/ai/sales-text', {
        productName,
        productDescription,
        tone,
        language: 'de',
      })
      setResult(res.data.text)
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Fehler bei der Textgenerierung'
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }, [])

  return { generate, isLoading, error, result, setResult }
}

export function AiTextsSection() {
  const [productName, setProductName] = useState('')
  const [productDescription, setProductDescription] = useState('')
  const [tone, setTone] = useState<'professional' | 'simple' | 'technical'>('professional')
  const [copied, setCopied] = useState(false)
  const { generate, isLoading, error, result } = useGenerateSalesText()

  const handleGenerate = () => {
    if (!productName.trim() || !productDescription.trim()) return
    generate(productName, productDescription, tone)
  }

  const handleCopy = async () => {
    if (!result) return
    await navigator.clipboard.writeText(result)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-500" />
          KI-gestützte Vertriebstexte
        </h3>
        <p className="text-xs text-gray-500 mb-4">Generiere Verkaufstexte für Angebote basierend auf Artikeldaten.</p>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Produktname</label>
            <input
              type="text"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder="z.B. HP ProLiant DL380 Gen10 Plus Server"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Artikelbeschreibung / Datanorm-Text</label>
            <textarea
              value={productDescription}
              onChange={(e) => setProductDescription(e.target.value)}
              rows={3}
              placeholder="z.B. 2x Intel Xeon Silver 4314, 64GB RAM, 2x 960GB SSD, Redundante Netzteile..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
            />
          </div>

          <div className="flex items-center gap-3">
            <label className="text-xs font-medium text-gray-600">Ton:</label>
            {(['professional', 'simple', 'technical'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTone(t)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
                  tone === t ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                )}
              >
                {t === 'professional' ? 'Professionell' : t === 'simple' ? 'Einfach' : 'Technisch'}
              </button>
            ))}
          </div>

          <button
            onClick={handleGenerate}
            disabled={isLoading || !productName.trim() || !productDescription.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {isLoading ? 'Generiere...' : 'Text generieren'}
          </button>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {result && (
          <div className="mt-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{result}</p>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button
                variant="secondary"
                size="xs"
                onClick={handleCopy}
                leadingIcon={copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
              >
                {copied ? 'Kopiert' : 'Kopieren'}
              </Button>
              <Button
                variant="primary"
                size="xs"
                leadingIcon={<FileText className="w-3.5 h-3.5" />}
              >
                In Angebot übernehmen
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── AI Text Modal ──────────────────────────────────────────────────────────

export function AiTextModal({ article, onClose }: { article: Article; onClose: () => void }) {
  const [tone, setTone] = useState<'professional' | 'simple' | 'technical'>('professional')
  const [copied, setCopied] = useState(false)
  const { generate, isLoading, error, result } = useGenerateSalesText()

  const handleGenerate = () => {
    const description = [
      article.itemName,
      article.manufacturer ? `Hersteller: ${article.manufacturer}` : '',
      article.groupName ? `Kategorie: ${article.groupName}` : '',
    ].filter(Boolean).join('. ')
    generate(article.itemName, description, tone)
  }

  const handleCopy = async () => {
    if (!result) return
    await navigator.clipboard.writeText(result)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              KI-Text generieren
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">{article.itemName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs font-medium text-gray-500 mb-1">Artikel</p>
            <p className="text-sm font-medium text-gray-900">{article.itemName}</p>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
              <span className="font-mono">{article.itemCode}</span>
              {article.manufacturer && <span>{article.manufacturer}</span>}
              {article.groupName && <span>{article.groupName}</span>}
              <span className="font-medium text-gray-700">{formatCurrency(article.salesPrice)}</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="text-xs font-medium text-gray-600">Ton:</label>
            {(['professional', 'simple', 'technical'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTone(t)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
                  tone === t ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                )}
              >
                {t === 'professional' ? 'Professionell' : t === 'simple' ? 'Einfach' : 'Technisch'}
              </button>
            ))}
          </div>

          <button
            onClick={handleGenerate}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {isLoading ? 'Generiere...' : 'Text generieren'}
          </button>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {result && (
            <div>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{result}</p>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg transition-colors"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Kopiert' : 'Kopieren'}
                </button>
                <button className="flex items-center gap-2 px-3 py-1.5 bg-brand-500 hover:bg-brand-600 text-white text-xs font-medium rounded-lg transition-colors">
                  <FileText className="w-3.5 h-3.5" />
                  In Angebot übernehmen
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
